/**
 * Hooks TanStack Query para el módulo de Inventario.
 * Patrón: tipos + query hooks (read) + mutation hooks (write).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../../lib/apiClient'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Warehouse {
  id: string
  code: string
  name: string
  address: string
  responsible_user: string | null
  is_default: boolean
  is_active: boolean
  stock_items_count?: number
  created_at: string
  updated_at?: string
}

export interface WarehouseStock {
  id: string
  warehouse_id: string
  warehouse_code: string
  warehouse_name: string
  product_id: string
  product_sku: string
  product_name: string
  quantity: string
  reserved_quantity: string
  available_quantity: string
  avg_cost: string
  reorder_point: string
  last_movement_date: string | null
  updated_at: string
}

export interface StockMovement {
  id: string
  warehouse_code: string
  warehouse_name: string
  product_sku: string
  product_name: string
  movement_type: string
  movement_type_display: string
  quantity: string
  unit_cost: string
  total_cost: string
  reference_type: string
  reference_id: string | null
  status: string
  notes: string
  created_at: string
}

export interface StockCount {
  id: string
  warehouse: string
  warehouse_code: string
  warehouse_name: string
  count_date: string
  status: string
  lines: StockCountLine[]
  lines_count?: number
  differences_count?: number
  notes: string
  counted_by: string | null
  completed_at: string | null
  accounting_status: string
  created_at: string
  updated_at?: string
}

export interface StockCountLine {
  product_id: string
  product_sku: string
  product_name: string
  expected_qty: string
  counted_qty: string
  difference: string
  unit_cost: string
}

export interface StockTransfer {
  id: string
  from_warehouse: string
  from_warehouse_code: string
  from_warehouse_name: string
  to_warehouse: string
  to_warehouse_code: string
  to_warehouse_name: string
  status: string
  transfer_date: string | null
  received_date: string | null
  lines: StockTransferLine[]
  lines_count?: number
  notes: string
  dispatched_by: string | null
  received_by: string | null
  created_at: string
  updated_at?: string
}

export interface StockTransferLine {
  product_id: string
  product_sku: string
  product_name: string
  quantity: string
  unit_cost?: string
}

export interface ValuationItem {
  warehouse_code: string
  warehouse_name: string
  product_sku: string
  product_name: string
  quantity: string
  avg_cost: string
  total_value: string
}

interface ListResponse<T> {
  count: number
  next: string | null
  previous: string | null
}

// ─── Warehouse Hooks ────────────────────────────────────────────────────────

export function useWarehouses(params: {
  search?: string
  active?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['warehouses', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/warehouses/', { params })
      return res.data as { warehouses: Warehouse[]; count: number }
    },
  })
}

export function useWarehouse(id: string) {
  return useQuery({
    queryKey: ['warehouses', id],
    queryFn: async () => {
      const res = await apiClient.get(`/inventory/warehouses/${id}/`)
      return res.data as Warehouse
    },
    enabled: !!id,
  })
}

export function useCreateWarehouse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Warehouse>) => {
      const res = await apiClient.post('/inventory/warehouses/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  })
}

export function useUpdateWarehouse(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Warehouse>) => {
      const res = await apiClient.patch(`/inventory/warehouses/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  })
}

// ─── Stock Hooks ────────────────────────────────────────────────────────────

export function useWarehouseStock(warehouseId: string, params?: {
  search?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['warehouse-stock', warehouseId, params],
    queryFn: async () => {
      const res = await apiClient.get(`/inventory/warehouses/${warehouseId}/stock/`, { params })
      return res.data as { stock: WarehouseStock[]; count: number }
    },
    enabled: !!warehouseId,
  })
}

export function useAllStock(params: {
  search?: string
  warehouse_id?: string
  low_stock?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['all-stock', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/stock/', { params })
      return res.data as { stock: WarehouseStock[]; count: number }
    },
  })
}

// ─── Locations ──────────────────────────────────────────────────────────────

export interface StorageLocation {
  id: string
  warehouse: string
  warehouse_code: string
  warehouse_name: string
  code: string
  name: string
  zone: string
  is_active: boolean
}

export function useWarehouseLocations(warehouseId: string) {
  return useQuery({
    queryKey: ['warehouse-locations', warehouseId],
    queryFn: async () => {
      const res = await apiClient.get(`/inventory/warehouses/${warehouseId}/locations/`)
      return res.data as { locations: StorageLocation[]; count: number }
    },
    enabled: !!warehouseId,
  })
}

export function useUpdateStockLocation(stockId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (locationId: string | null) => {
      const res = await apiClient.patch(`/inventory/stock/${stockId}/`, { location: locationId })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
    },
  })
}

// ─── Movement Hooks ─────────────────────────────────────────────────────────

export function useMovements(params: {
  ordering?: string
  warehouse_id?: string
  product_id?: string
  movement_type?: string
  search?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['movements', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/movements/', { params })
      return res.data as { movements: StockMovement[]; count: number }
    },
  })
}

export function useCreateMovement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      warehouse_id: string
      product_id: string
      movement_type: string
      quantity: number | string
      unit_cost?: number | string
      notes?: string
    }) => {
      const res = await apiClient.post('/inventory/movements/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movements'] })
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
    },
  })
}

export function useMovement(id: string) {
  return useQuery({
    queryKey: ['movements', id],
    queryFn: async () => {
      const res = await apiClient.get(`/inventory/movements/${id}/`)
      return res.data as StockMovement
    },
    enabled: !!id,
  })
}

export function useCancelMovement(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/inventory/movements/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movements'] })
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
      qc.invalidateQueries({ queryKey: ['kardex'] })
    },
  })
}

// ─── Kardex Hook ────────────────────────────────────────────────────────────

export function useKardex(params: {
  product_id?: string
  warehouse_id?: string
  date_from?: string
  date_to?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['kardex', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/kardex/', { params })
      return res.data as { movements: StockMovement[]; count: number }
    },
    enabled: !!params.product_id || !!params.warehouse_id,
  })
}

// ─── Stock Count Hooks ──────────────────────────────────────────────────────

export function useStockCounts(params: {
  ordering?: string
  warehouse_id?: string
  status?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['stock-counts', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/counts/', { params })
      return res.data as { counts: StockCount[]; count: number }
    },
  })
}

export function useStockCount(id: string) {
  return useQuery({
    queryKey: ['stock-counts', id],
    queryFn: async () => {
      const res = await apiClient.get(`/inventory/counts/${id}/`)
      return res.data as StockCount
    },
    enabled: !!id,
  })
}

export function useCreateStockCount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<StockCount>) => {
      const res = await apiClient.post('/inventory/counts/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-counts'] }),
  })
}

export function useUpdateStockCount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<StockCount>) => {
      const res = await apiClient.patch(`/inventory/counts/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-counts'] }),
  })
}

export function useCompleteStockCount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/inventory/counts/${id}/complete/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-counts'] })
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
    },
  })
}

export function useCancelStockCount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/inventory/counts/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-counts'] }),
  })
}

// ─── Transfer Hooks ─────────────────────────────────────────────────────────

export function useTransfers(params: {
  status?: string
  warehouse_id?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['transfers', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/transfers/', { params })
      return res.data as { transfers: StockTransfer[]; count: number }
    },
  })
}

export function useTransfer(id: string) {
  return useQuery({
    queryKey: ['transfers', id],
    queryFn: async () => {
      const res = await apiClient.get(`/inventory/transfers/${id}/`)
      return res.data as StockTransfer
    },
    enabled: !!id,
  })
}

export function useCreateTransfer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<StockTransfer>) => {
      const res = await apiClient.post('/inventory/transfers/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfers'] }),
  })
}

export function useUpdateTransfer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<StockTransfer>) => {
      const res = await apiClient.patch(`/inventory/transfers/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfers'] }),
  })
}

export function useDispatchTransfer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/inventory/transfers/${id}/dispatch/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] })
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
    },
  })
}

export function useReceiveTransfer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/inventory/transfers/${id}/receive/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] })
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
    },
  })
}

export function useCancelTransfer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/inventory/transfers/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] })
      qc.invalidateQueries({ queryKey: ['all-stock'] })
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] })
    },
  })
}

// ─── Valuation Hook ─────────────────────────────────────────────────────────

export function useValuation(params?: { warehouse_id?: string }) {
  return useQuery({
    queryKey: ['valuation', params],
    queryFn: async () => {
      const res = await apiClient.get('/inventory/valuation/', { params })
      return res.data as {
        items: ValuationItem[]
        total_value: string
        count: number
      }
    },
  })
}
